/**
 * AerialWanderAction - behavior tree action for aerial wandering behavior.
 */

import * as THREE from 'three';

import { AerialMovementComponent } from '../../../../../components/movement/AerialMovementComponent.js';
import { EngineClass } from '../../../../../systems/ClassRegistry.js';
import { BehaviorStatus } from '../../../BehaviorNode.js';
import { BehaviorAction, type BehaviorActionOptions } from '../../BehaviorAction.js';

import type { Blackboard } from '../../../blackboard/Blackboard.js';

export interface AerialWanderActionOptions extends BehaviorActionOptions {
  wanderRadius?: number;
  wanderHeight?: number;
  heightVariation?: number;
  waitTime?: number;
  speedMultiplier?: number;
}

@EngineClass('AerialWanderAction')
export class AerialWanderAction extends BehaviorAction {
  private wanderRadius: number;
  private wanderHeight: number;
  private heightVariation: number;
  private waitTime: number;
  private speedMultiplier: number;
  private movementComponent: AerialMovementComponent | null = null;
  private spawnPosition: THREE.Vector3 | null = null;
  private currentWanderTarget: THREE.Vector3 | null = null;
  private waitStartTime: number = 0;
  private isWaiting: boolean = false;

  constructor(options: AerialWanderActionOptions = {}) {
    super({
      name: 'AerialWander',
      ...options
    });

    this.wanderRadius = options.wanderRadius ?? 30.0;
    this.wanderHeight = options.wanderHeight ?? 20.0;
    this.heightVariation = options.heightVariation ?? 8.0;
    this.waitTime = options.waitTime ?? 3.0;
    this.speedMultiplier = options.speedMultiplier ?? 0.7;
  }

  protected override onInitialize(blackboard: Blackboard): void {
    const owner = this.getOwner(blackboard);
    if (!owner) {
      return;
    }

    this.movementComponent = owner.getComponent(AerialMovementComponent);
    if (!this.movementComponent) {
      console.warn('AerialWanderAction: No AerialMovementComponent found on actor');
      return;
    }

    // Store spawn position for wandering around
    if (!this.spawnPosition) {
      this.spawnPosition = new THREE.Vector3();
      owner.rootComponent.getWorldPosition(this.spawnPosition);
    }
  }

  protected override onEnter(blackboard: Blackboard): void {
    // Generate new wander target (or continue with existing one if still valid)
    if (!this.currentWanderTarget || this.isWaiting) {
      this.generateNewWanderTarget();
    }
    this.isWaiting = false;
  }

  protected override async onUpdate(blackboard: Blackboard, deltaTime: number): Promise<BehaviorStatus> {
    const owner = this.getOwner(blackboard);
    if (!owner || !this.movementComponent || !this.spawnPosition) {
      return BehaviorStatus.Failure;
    }

    const currentTime = Date.now() / 1000;
    const ownerPosition = new THREE.Vector3();
    owner.rootComponent.getWorldPosition(ownerPosition);

    if (this.isWaiting) {
      // Wait at current position
      const waitElapsed = currentTime - this.waitStartTime;
      if (waitElapsed >= this.waitTime) {
        // Done waiting, complete the action to allow behavior tree re-evaluation
        return BehaviorStatus.Success;
      }
      return BehaviorStatus.Running;
    }

    if (this.currentWanderTarget) {
      // Move toward current wander target
      this.movementComponent.setTargetPosition(this.currentWanderTarget);

      // Check if we've reached the target
      const distance = ownerPosition.distanceTo(this.currentWanderTarget);
      if (distance <= 3.0) { // Close enough to target
        // Start waiting
        this.movementComponent.clearTarget();
        this.isWaiting = true;
        this.waitStartTime = currentTime;
      }
    } else {
      // No target, generate one
      this.generateNewWanderTarget();
    }

    return BehaviorStatus.Running;
  }

  protected override onExit(blackboard: Blackboard, status: BehaviorStatus): void {
    // Clear movement target
    if (this.movementComponent) {
      this.movementComponent.clearTarget();
    }
  }

  private generateNewWanderTarget(): void {
    if (!this.spawnPosition) {
      return;
    }

    // Generate random point within wander radius
    const angle = Math.random() * Math.PI * 2;
    const distance = Math.random() * this.wanderRadius;

    const x = this.spawnPosition.x + Math.cos(angle) * distance;
    const z = this.spawnPosition.z + Math.sin(angle) * distance;
    const y = this.wanderHeight + (Math.random() - 0.5) * this.heightVariation;

    this.currentWanderTarget = new THREE.Vector3(x, y, z);
  }

  public getCurrentWanderTarget(): THREE.Vector3 | null {
    return this.currentWanderTarget?.clone() ?? null;
  }

  public isCurrentlyWaiting(): boolean {
    return this.isWaiting;
  }
}
